import { forwardRef } from 'react'
import Link from 'next/link'

import type { NavigationItemFragment } from '@/lib/types/datocms/graphql/generated'
import { linkResolver } from '@/utils/datocms/links'
import type { Route } from 'next'

type Props = {
	cta?: NavigationItemFragment
	onClick?: React.MouseEventHandler<HTMLButtonElement>
	children?: React.ReactNode
	className?: string
	closeMegaMenu?: boolean
	isInternational?: boolean
	type?: string
	disabled?: boolean
	tabIndex?: number
}

const Cta = forwardRef<HTMLAnchorElement | HTMLButtonElement, Props>(
	(
		{ cta, children, className, onClick, closeMegaMenu, isInternational, type, disabled, tabIndex },
		ref
	) => {
		const linkTargetProps = cta?.openInNewWindow
			? {
					target: '_blank',
					rel: 'noopener noreferrer',
					'aria-label': cta.label ?? '',
				}
			: {}

		const href = cta?.useExternalLink
			? cta?.externalLink
			: linkResolver(cta?.internalLink, isInternational)

		if (!href) return null

		if (closeMegaMenu && onClick && href) {
			return (
				<Link
					href={href as Route}
					{...linkTargetProps}
					ref={ref as React.Ref<HTMLAnchorElement>}
					className={className}
					onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
						if (onClick) onClick(e as unknown as React.MouseEvent<HTMLButtonElement>)
					}}
				>
					{children}
				</Link>
			)
		}

		if (type === 'submit') {
			return (
				<button type="submit" className={className} onClick={onClick} disabled={disabled}>
					{children}
				</button>
			)
		}

		// Ex. Navigation Dropdown Buttons
		if (onClick) {
			return (
				<button
					className={className}
					onClick={onClick}
					ref={ref as React.Ref<HTMLButtonElement>}
					tabIndex={tabIndex}
				>
					{children}
				</button>
			)
		}

		return (
			<Link
				href={href as Route}
				{...linkTargetProps}
				ref={ref as React.Ref<HTMLAnchorElement>}
				className={className}
			>
				{children}
			</Link>
		)
	}
)

Cta.displayName = 'CTA'

export default Cta
